import produce from 'immer';

import BenchlingConfig from 'BenchlingConfig';
import {ContextMenuOption} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CustomStructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID} from 'notebook/tables/core-ui/TableAxisCell';
import {
  ColumnConstraint,
  StructuredTableToTableMetadata,
  Table,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';

import {logColumnAnalytics} from '../util/StructuredTableAnalyticsUtil';
import {CustomColumnDataType, getCustomColumnDataTypeDisplayName} from './CustomColumnConstants';

/**
 * Define menu options for changing the data type of an existing custom column.
 *
 * This menu option appears as a submenu with a chevron icon, allowing inline
 * selection of the custom column type without opening a modal.
 */
export default function getChangeCustomColumnTypeMenuOptions<TTableMetadata>(
  table: Table,
  tableIsLocked: boolean,
  selectionRange: SelectionRange | null,
  updateColumnConstraint: (columnIndex: number, columnConstraint: ColumnConstraint) => void,
  fileAnalyticsParams: FileAnalyticsParams
): Array<ContextMenuOption> {
  const tableMetadata = table.metadata as StructuredTableToTableMetadata<TTableMetadata>;
  const structuredTableAnalyticsParams = tableMetadata
    ? {
        structuredTableKind: tableMetadata.kind,
        structuredTableProvenanceUuid: tableMetadata.provenanceUuid,
      }
    : null;

  // Only show when the feature flag is enabled and a single custom column is selected
  const shouldShowChangeTypeOption = (): boolean => {
    if (!BenchlingConfig.ELN_ENABLE_CHANGE_CUSTOM_COLUMN_DATA_TYPE) {
      return false;
    }
    if (tableIsLocked) {
      return false;
    }
    if (selectionRange == null || selectionRange.minIndex !== selectionRange.maxIndex) {
      return false;
    }
    const columnConstraint = table.columnConstraints[selectionRange.minIndex];
    if (columnConstraint == null) {
      return false;
    }
    const {structuredTableColumn} = columnConstraint;
    return structuredTableColumn?.columnType === TableColumnType.CUSTOM && !structuredTableColumn.isProtected;
  };

  if (!shouldShowChangeTypeOption()) {
    return [];
  }

  const selectedColumnIndex = selectionRange!.minIndex;
  const selectedColumnConstraint = table.columnConstraints[selectedColumnIndex]!;
  const selectedColumn = selectedColumnConstraint.structuredTableColumn as CustomStructuredTableColumn;
  const currentDataType = selectedColumn.customColumnConfig.dataType;

  const changeDataType = (newDataType: CustomColumnDataType): void => {
    if (newDataType === currentDataType) {
      return; // No change needed
    }

    // Update the column constraint with the new data type
    const updatedColumnConstraint = produce(selectedColumnConstraint, (draft) => {
      if (draft.structuredTableColumn?.columnType === TableColumnType.CUSTOM) {
        (draft.structuredTableColumn as CustomStructuredTableColumn).customColumnConfig.dataType =
          newDataType;
      }
      // Also update the column definition if it exists
      if (
        draft.columnDefinition?.type === TableColumnType.CUSTOM &&
        draft.columnDefinition.customColumnConfig
      ) {
        draft.columnDefinition.customColumnConfig.dataType = newDataType;
      }
    });

    logColumnAnalytics({
      analyticsEvent: ELN_ANALYTICS_ACTIONS.CHANGED_CUSTOM_COLUMN_TYPE,
      fileAnalyticsParams,
      structuredTableAnalyticsParams,
      additionalMetadata: {
        customColumnProvenanceUuid: selectedColumn.provenanceUuid,
        customColumnName: selectedColumn.name,
        previousDataType: getCustomColumnDataTypeDisplayName(currentDataType),
        newDataType: getCustomColumnDataTypeDisplayName(newDataType),
      },
    });

    updateColumnConstraint(selectedColumnIndex, updatedColumnConstraint);
  };

  // Create submenu options for each data type
  const dataTypeSubmenuOptions: Array<ContextMenuOption> = Object.values(CustomColumnDataType).map(
    (dataType: CustomColumnDataType) => ({
      name: getCustomColumnDataTypeDisplayName(dataType),
      click: () => {
        changeDataType(dataType);
      },
      active: () => dataType === currentDataType,
      dataTestAttrs: {
        component: 'ContextMenu',
        element: 'changeCustomColumnType',
        key: dataType,
      },
    })
  );

  return [
    {
      id: CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID,
      name: 'Change column type',
      menu: dataTypeSubmenuOptions,
      // Note: displayGroup is intentionally not set. This option is manually positioned
      // in TableAxisCell.tsx to appear right after "Rename column"
      dataTestAttrs: {
        component: 'ContextMenu',
        element: 'changeCustomColumnTypeMenu',
      },
    },
  ];
}
